import { isQuoteRequestClosed } from "@/features/quote-requests/domain/quoteRequestClosed";

/**
 * 견적 철회(V-13) 판정 — 공급사 화면용.
 *
 * 규칙(총괄 결정 10-06): 공고가 진행 중(마감 전)이고 계약 전인 **내 견적**만 철회할 수 있다.
 * 서버(`POST /quote-responses/:id/withdraw`)가 같은 규칙으로 다시 막으므로, 여기서는 버튼을
 * 보일지 말지만 정한다.
 *
 * 공급사 API(`GET /external/quote-requests/:id`)는 공급사에게 **자기 견적만** `responses` 에 담아
 * 주고, `contracts` 도 자기 계약만 준다. 그래서 첫 번째 응답이 곧 내 견적이다.
 */

/** 상세 응답 중 철회 판정에 쓰는 칸만. 생성 타입에 아직 없는 `withdrawnAt` 을 함께 받는다. */
export interface WithdrawalSourceRequest {
  status?: string | null;
  deadline?: string | null;
  isClosed?: boolean | null;
  closedAt?: string | null;
  responses?:
    | readonly {
        id: string;
        isContracted?: boolean | null;
        withdrawnAt?: string | null;
      }[]
    | null;
  contracts?: readonly { isCanceled?: boolean | null }[] | null;
}

export interface MyQuoteWithdrawalState {
  quoteResponseId: string;
  /** 철회한 시각. null 이면 철회 안 됨 */
  withdrawnAt: string | null;
  /** 공고 마감(수동 마감 또는 마감일 경과) */
  isClosed: boolean;
  /** 취소되지 않은 계약이 있다 */
  hasActiveContract: boolean;
}

/** 내 견적의 철회 상태. 아직 견적을 낸 적이 없으면 null. */
export function getMyQuoteWithdrawalState(
  request: WithdrawalSourceRequest,
  now: Date = new Date(),
): MyQuoteWithdrawalState | null {
  const myResponse = request.responses?.[0];
  if (!myResponse?.id) return null;

  // 계약 목록이 오면 그것이 정본이다(취소된 계약은 빼고 본다). 목록이 없는 옛 응답만
  // 채팅방 기준 `isContracted` 로 물러난다.
  const hasActiveContract = request.contracts
    ? request.contracts.some((contract) => contract.isCanceled !== true)
    : myResponse.isContracted === true;

  return {
    quoteResponseId: myResponse.id,
    withdrawnAt: myResponse.withdrawnAt ?? null,
    isClosed: !!request.closedAt || isQuoteRequestClosed(request, now),
    hasActiveContract,
  };
}

/** 철회 버튼을 보일 수 있는가 — 철회 전 · 마감 전 · 계약 전. */
export function canWithdrawQuote(state: MyQuoteWithdrawalState): boolean {
  return !state.withdrawnAt && !state.isClosed && !state.hasActiveContract;
}

export const WITHDRAWAL_REASON_MAX_LENGTH = 500;
